import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ff018244-580d-47eb-b754-fa20aec11c8d/artifacts/2zc3j2vv_jini%20studio%20logo%20.png";

export default function RefundPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <nav className="bg-white border-b border-neutral-100 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Jini Studio" className="h-16 w-auto" />
          </Link>
          <Link to="/">
            <Button variant="ghost" className="gap-2">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Button>
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="font-display text-4xl font-bold text-neutral-900 mb-8">Refund Policy</h1>
        <p className="text-sm text-neutral-500 mb-8">Last updated: February 1, 2026</p>

        <div className="prose prose-neutral max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. Overview</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              At Jini Studio AI, we strive to provide high-quality AI-generated photoshoots for our customers. This Refund Policy outlines the circumstances under which refunds may be requested and processed.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. Subscription Refunds</h2>
            
            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Cancellation Policy</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">
              You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of the current billing period. No refunds are provided for:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4 mb-4">
              <li>Partial month usage</li>
              <li>Unused SKU credits</li>
              <li>Early cancellation during a billing cycle</li>
            </ul>

            <h3 className="text-lg font-semibold text-neutral-800 mb-3">First-Time Users</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">
              New subscribers may request a full refund within the first 7 days of their subscription if:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li>They have not used more than 3 SKU credits</li>
              <li>They have not downloaded any generated assets</li>
              <li>They provide a valid reason for their refund request</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. Service Quality Refunds</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We may provide refunds or SKU credits in the following circumstances:
            </p>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <h4 className="font-semibold text-green-800 mb-2">Eligible for Refund/Credit</h4>
              <ul className="list-disc list-inside text-green-700 space-y-2">
                <li>Technical failures that prevent image/video generation</li>
                <li>Significant quality issues that make outputs unusable</li>
                <li>Processing delays exceeding 24 hours for standard queue</li>
                <li>Duplicate charges or billing errors</li>
              </ul>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-semibold text-red-800 mb-2">Not Eligible for Refund</h4>
              <ul className="list-disc list-inside text-red-700 space-y-2">
                <li>Dissatisfaction with AI model appearance or style</li>
                <li>Low-quality source images uploaded by the user</li>
                <li>User error in selecting models or backgrounds</li>
                <li>Change of mind after generation is complete</li>
                <li>Requests made more than 7 days after generation</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. Requesting a Refund</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">To request a refund:</p>
            <ol className="list-decimal list-inside text-neutral-600 space-y-3 ml-4">
              <li>Contact our support team at support@jinistudio.ai within 7 days of the issue</li>
              <li>Provide your account email and order/shoot ID</li>
              <li>Describe the issue in detail with screenshots if applicable</li>
              <li>Our team will review your request within 3 business days</li>
            </ol>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">5. Refund Processing</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              Approved refunds will be processed as follows:
            </p>
            <ul className="list-disc list-inside text-neutral-600 space-y-2 ml-4">
              <li><strong>Payment Refunds:</strong> Credited back to the original payment method within 5-7 business days</li>
              <li><strong>SKU Credits:</strong> Added to your account immediately upon approval</li>
              <li><strong>Partial Refunds:</strong> May be offered for issues affecting part of an order</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">6. Special Circumstances</h2>
            
            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Service Outages</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">
              In case of extended service outages (more than 24 hours), affected subscribers will receive proportional SKU credits for the downtime period.
            </p>

            <h3 className="text-lg font-semibold text-neutral-800 mb-3">Beta Features</h3>
            <p className="text-neutral-600 leading-relaxed mb-4">
              SKUs used on beta or experimental features may be refunded as credits if the results are unsatisfactory. This is determined on a case-by-case basis.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">7. Contact Information</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              For refund requests or questions about this policy:
            </p>
            <div className="bg-neutral-50 rounded-lg p-4 text-neutral-600">
              <p className="font-medium">Jini Studio AI Support</p>
              <p>Email: support@jinistudio.ai</p>
              <p>Response Time: Within 24 hours (business days)</p>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-neutral-900 mb-4">8. Changes to This Policy</h2>
            <p className="text-neutral-600 leading-relaxed mb-4">
              We reserve the right to modify this Refund Policy at any time. Changes will be effective immediately upon posting to this page. Continued use of the Service after changes constitutes acceptance of the updated policy.
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-neutral-100 py-8 px-6 mt-12">
        <div className="max-w-4xl mx-auto text-center text-neutral-600 text-sm">
          <p>© 2025 Jini Studio by Zaroori Retail. All rights reserved.</p>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/terms" className="hover:text-indigo-600">Terms & Conditions</Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-indigo-600">Privacy Policy</Link>
            <span>•</span>
            <Link to="/contact" className="hover:text-indigo-600">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
